import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, ChevronRight, Bot, Briefcase, BookOpen, Dumbbell, Lightbulb, GraduationCap, ShoppingBag, Sparkles, Zap, Cpu } from "lucide-react";

const LOGO = "/ashra-logo.png";

const STEPS = ["Niche & Business", "Revenue Goal", "Connect Channels", "AI Team Setup", "Automation Preferences", "Review & Launch"];

const NICHES = [
  { id: "coaching", label: "Coaching", icon: Briefcase },
  { id: "digital_products", label: "Digital Products", icon: ShoppingBag },
  { id: "education", label: "Education", icon: BookOpen },
  { id: "fitness", label: "Fitness", icon: Dumbbell },
  { id: "consulting", label: "Consulting", icon: Lightbulb },
  { id: "other", label: "Other", icon: GraduationCap },
];

const CHANNELS = [
  { id: "instagram", label: "Instagram", color: "from-pink-500 to-orange-500" },
  { id: "whatsapp", label: "WhatsApp", color: "from-emerald-500 to-teal-500" },
  { id: "email", label: "Email", color: "from-blue-500 to-indigo-500" },
  { id: "telegram", label: "Telegram", color: "from-sky-400 to-blue-500" },
];

const CURRENCIES = [
  { id: "usd", label: "USD · $", note: "Stripe" },
  { id: "inr", label: "INR · ₹", note: "Razorpay" },
];

export default function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [reveal, setReveal] = useState(null);          // Set once /onboarding returns
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    niche: "", revenue_goal: 300000, channels: [], ai_tone: "friendly", autonomy: "assisted", currency: "usd",
  });

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const toggleChannel = (id) => setForm((f) => ({
    ...f, channels: f.channels.includes(id) ? f.channels.filter(x => x !== id) : [...f.channels, id],
  }));

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/onboarding", form);
      // Fetch the personalized reveal payload (persona + starter automations).
      const { data } = await api.get("/onboarding/preview");
      setReveal(data);
    } catch {
      toast.error("Could not save your setup — please try again.");
      setSubmitting(false);
    }
  };

  const stepValid = () => {
    if (step === 0) return !!form.niche;
    if (step === 2) return form.channels.length > 0;
    return true;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50" data-testid="onboarding-page">
      {reveal && <RevealScreen reveal={reveal} form={form} onEnter={() => nav("/dashboard")} />}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 left-1/3 h-[28rem] w-[28rem] rounded-full blur-3xl opacity-20 ashra-gradient-bg" />
      </div>
      <div className="relative mx-auto max-w-5xl px-6 py-10">
        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO} alt="Ashra AI" className="h-9" />
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          {/* Steps */}
          <div className="space-y-2">
            {STEPS.map((s, i) => (
              <div key={s} className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm ${i === step ? "bg-white border border-violet-300 shadow-sm text-violet-700" : i < step ? "text-emerald-600" : "text-zinc-500"}`} data-testid={`step-indicator-${i}`}>
                <span className={`h-7 w-7 rounded-full grid place-items-center text-xs font-bold ${i < step ? "bg-emerald-100 text-emerald-600" : i === step ? "ashra-gradient-bg text-white" : "bg-zinc-100 text-zinc-500"}`}>
                  {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                {s}
              </div>
            ))}
          </div>

          {/* Form card */}
          <motion.div key={step} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}
            className="rounded-3xl bg-white border border-black/5 shadow-xl shadow-violet-500/5 p-8" data-testid={`step-${step}`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs text-violet-600 uppercase tracking-wider font-medium">Step {step + 1} / {STEPS.length}</p>
                <h2 className="mt-1 font-display text-2xl font-bold">Let's set up your AI Revenue Team <span>👋</span></h2>
              </div>
              <div className="h-12 w-12 rounded-2xl ashra-gradient-bg grid place-items-center text-white"><Bot className="h-5 w-5" /></div>
            </div>

            {step === 0 && (
              <div>
                <p className="text-sm text-zinc-500 mb-4">We'll personalize everything for maximum results.</p>
                <p className="text-sm font-medium mb-3">What's your niche?</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {NICHES.map((n) => (
                    <button key={n.id} type="button" onClick={() => setForm({ ...form, niche: n.id })}
                      data-testid={`niche-${n.id}`}
                      className={`rounded-2xl border p-4 text-left transition ${form.niche === n.id ? "border-violet-400 bg-violet-50 text-violet-700" : "border-zinc-200 hover:border-zinc-300"}`}>
                      <n.icon className="h-5 w-5 mb-2" />
                      <p className="text-sm font-medium">{n.label}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <p className="text-sm font-medium mb-2">What's your monthly revenue goal?</p>
                <p className="font-display text-5xl font-extrabold text-violet-600 mb-4">
                  {form.currency === "inr" ? "₹" : "$"}{form.revenue_goal.toLocaleString()}
                </p>
                <input type="range" min={10000} max={1000000} step={10000} value={form.revenue_goal}
                  onChange={(e) => setForm({ ...form, revenue_goal: parseInt(e.target.value) })}
                  className="w-full accent-violet-600" data-testid="revenue-goal-slider" />
                <div className="mt-2 flex justify-between text-xs text-zinc-500">
                  <span>{form.currency === "inr" ? "₹50K" : "$10K"}</span>
                  <span>{form.currency === "inr" ? "₹10L+" : "$1M+"}</span>
                </div>
                <div className="mt-6">
                  <p className="text-sm font-medium mb-2">Currency</p>
                  <div className="inline-flex rounded-full border border-zinc-200 p-1">
                    {CURRENCIES.map((c) => (
                      <button key={c.id} onClick={() => setForm({ ...form, currency: c.id })}
                        className={`px-4 py-1.5 text-xs rounded-full ${form.currency === c.id ? "ashra-gradient-bg text-white" : "text-zinc-500"}`}
                        data-testid={`currency-${c.id}`}>
                        {c.label} <span className="opacity-60 ml-1">{c.note}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <p className="text-sm text-zinc-500 mb-4">Pick the channels Ashra should operate on.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {CHANNELS.map((c) => (
                    <button key={c.id} onClick={() => toggleChannel(c.id)} data-testid={`channel-${c.id}`}
                      className={`rounded-2xl p-4 border text-left transition ${form.channels.includes(c.id) ? "border-violet-400 bg-violet-50" : "border-zinc-200"}`}>
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${c.color} text-white grid place-items-center font-bold`}>{c.label[0]}</div>
                        <div>
                          <p className="text-sm font-medium">{c.label}</p>
                          <p className="text-xs text-zinc-500">{form.channels.includes(c.id) ? "Selected" : "Tap to add"}</p>
                        </div>
                        {form.channels.includes(c.id) && <Check className="ml-auto h-4 w-4 text-violet-600" />}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <p className="text-sm text-zinc-500 mb-4">Pick how your AI team should sound.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { id: "friendly", label: "Friendly", desc: "Warm & conversational" },
                    { id: "professional", label: "Professional", desc: "Polished & business-grade" },
                    { id: "energetic", label: "Energetic", desc: "High-energy & motivating" },
                    { id: "calm", label: "Calm", desc: "Measured & reassuring" },
                  ].map((t) => (
                    <button key={t.id} onClick={() => setForm({ ...form, ai_tone: t.id })} data-testid={`tone-${t.id}`}
                      className={`rounded-2xl p-4 border text-left ${form.ai_tone === t.id ? "border-violet-400 bg-violet-50 text-violet-700" : "border-zinc-200"}`}>
                      <p className="text-sm font-semibold">{t.label}</p>
                      <p className="text-xs text-zinc-500 mt-1">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <p className="text-sm text-zinc-500 mb-4">How independently should AI operate?</p>
                <div className="space-y-3">
                  {[
                    { id: "manual", label: "Manual approval", desc: "AI suggests; you approve every action" },
                    { id: "assisted", label: "Co-Pilot", desc: "Workforce acts with your review on critical decisions" },
                    { id: "autonomous", label: "Autopilot", desc: "Workforce runs approved workflows end-to-end" },
                  ].map((a) => (
                    <button key={a.id} onClick={() => setForm({ ...form, autonomy: a.id })} data-testid={`autonomy-${a.id}`}
                      className={`w-full text-left rounded-2xl p-4 border ${form.autonomy === a.id ? "border-violet-400 bg-violet-50" : "border-zinc-200"}`}>
                      <p className="text-sm font-semibold">{a.label}</p>
                      <p className="text-xs text-zinc-500 mt-1">{a.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 5 && (
              <div>
                <p className="text-sm text-zinc-500 mb-4">All set. Launch your AI Revenue Team.</p>
                <div className="rounded-2xl bg-zinc-50 border border-zinc-200 p-5 space-y-2 text-sm">
                  <Row k="Niche" v={form.niche.replaceAll("_", " ")} />
                  <Row k="Revenue goal" v={`${form.currency === "inr" ? "₹" : "$"}${form.revenue_goal.toLocaleString()}/mo`} />
                  <Row k="Channels" v={form.channels.join(", ") || "—"} />
                  <Row k="AI tone" v={form.ai_tone} />
                  <Row k="Autonomy" v={form.autonomy} />
                </div>
                <button onClick={submit} disabled={submitting} className="mt-5 w-full rounded-full ashra-gradient-bg text-white py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70" data-testid="launch-ai-team">
                  {submitting ? "Teaching your AI…" : (<>Launch my AI Revenue Team <ChevronRight className="h-4 w-4" /></>)}
                </button>
              </div>
            )}

            {step < 5 && (
              <div className="mt-8 flex items-center justify-between">
                <button onClick={back} disabled={step === 0} className="text-sm text-zinc-500 disabled:opacity-40">Back</button>
                <button onClick={next} disabled={!stepValid()} className="inline-flex items-center gap-1 rounded-full ashra-gradient-bg text-white px-5 py-2 text-sm disabled:opacity-50" data-testid="next-btn">
                  Continue <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between"><span className="text-zinc-500 capitalize">{k}</span><span className="font-medium capitalize">{v}</span></div>;
}


/**
 * RevealScreen — the "Your AI has learned your voice" moment.
 * Renders on top of the Onboarding shell once the backend has persisted the
 * configuration and returned the seeded automations / channels.
 */
function RevealScreen({ reveal, form, onEnter }) {
  const [phase, setPhase] = React.useState(0);
  // Auto-advance through 3 reveal phases: 1) persona 2) team 3) launch button.
  React.useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 1400),
      setTimeout(() => setPhase(2), 3200),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const persona = reveal.ai_prompt_fragment || "";
  const autos = reveal.automations || [];
  const channels = reveal.channels || [];

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto" data-testid="onboarding-reveal">
      <div className="min-h-screen relative">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[36rem] w-[36rem] rounded-full blur-3xl opacity-25 ashra-gradient-bg" />
          <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full blur-3xl opacity-20 bg-gradient-to-br from-emerald-400 to-cyan-500" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 py-14">
          {/* Phase 0 → 1: persona reveal */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 rounded-full bg-violet-50 border border-violet-200 px-3 py-1 text-xs font-semibold text-violet-700">
              <Sparkles className="h-3.5 w-3.5" /> Your AI has learned your voice
            </div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-zinc-900 leading-tight">
              Meet your <span className="ashra-gradient-text">AI Revenue Team</span>.
            </h1>
            <p className="mt-3 text-lg text-zinc-500 max-w-2xl">
              Ashra just studied your niche, tone, and autonomy preferences. Here's the persona your AI will speak in — and the workforce it'll deploy from day one.
            </p>
          </motion.div>

          {/* Persona card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-8 rounded-3xl border border-violet-100 bg-white shadow-xl shadow-violet-500/5 p-6 sm:p-8"
            data-testid="reveal-persona"
          >
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-2xl ashra-gradient-bg grid place-items-center shrink-0 shadow-inner">
                <Cpu className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-violet-600 font-semibold">Your AI's voice</p>
                <p className="mt-1 text-sm text-zinc-500">
                  Niche: <span className="font-semibold text-zinc-900 capitalize">{(reveal.niche || form.niche || "").replaceAll("_", " ")}</span>
                  {" · "}Tone: <span className="font-semibold text-zinc-900 capitalize">{reveal.ai_tone || form.ai_tone}</span>
                  {" · "}Autonomy: <span className="font-semibold text-zinc-900 capitalize">{reveal.autonomy || form.autonomy}</span>
                </p>
              </div>
            </div>
            {persona && (
              <div className="mt-5 rounded-2xl bg-zinc-50 border border-zinc-100 p-4 text-sm text-zinc-700 leading-relaxed whitespace-pre-line font-mono">
                <TypedPersona text={persona} enabled={phase >= 0} />
              </div>
            )}
          </motion.div>

          {/* Phase 1: seeded automations + channels */}
          <AnimatePresence>
            {phase >= 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="mt-6 grid md:grid-cols-2 gap-4"
              >
                <div className="rounded-3xl border border-zinc-100 bg-white shadow-md shadow-zinc-200/40 p-6" data-testid="reveal-automations">
                  <p className="text-xs uppercase tracking-wider text-violet-600 font-semibold">Starter workforce ({autos.length})</p>
                  <p className="mt-1 text-sm text-zinc-500">Draft automations tuned to your niche. Activate any of them from Convert → Workforce.</p>
                  <ul className="mt-4 space-y-3">
                    {autos.map((a, idx) => (
                      <motion.li key={a.name} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 * idx + 0.2 }}
                        className="flex items-start gap-3 rounded-xl border border-zinc-100 bg-zinc-50/60 p-3">
                        <div className="h-8 w-8 rounded-lg bg-white border border-zinc-200 grid place-items-center shrink-0">
                          <Zap className="h-4 w-4 text-violet-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-zinc-900 truncate">{a.name}</p>
                          <p className="text-xs text-zinc-500 truncate">{a.trigger}</p>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full px-2 py-px font-bold">{a.status}</span>
                      </motion.li>
                    ))}
                    {autos.length === 0 && <li className="text-xs text-zinc-400 italic">No niche-specific starter automations were seeded.</li>}
                  </ul>
                </div>

                <div className="rounded-3xl border border-zinc-100 bg-white shadow-md shadow-zinc-200/40 p-6" data-testid="reveal-channels">
                  <p className="text-xs uppercase tracking-wider text-violet-600 font-semibold">Channels ready to connect</p>
                  <p className="mt-1 text-sm text-zinc-500">We've scaffolded these channels. Head to Settings → Integrations to authorise each one.</p>
                  <ul className="mt-4 space-y-3">
                    {channels.map((c, idx) => (
                      <motion.li key={c.kind} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 * idx + 0.2 }}
                        className="flex items-center gap-3 rounded-xl border border-zinc-100 p-3">
                        <div className="h-8 w-8 rounded-lg ashra-gradient-bg grid place-items-center text-white text-sm font-bold shrink-0">{c.kind?.[0]?.toUpperCase()}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-zinc-900 capitalize truncate">{c.kind}</p>
                          <p className="text-xs text-zinc-500">Requires OAuth — set up in Integrations.</p>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider bg-zinc-100 text-zinc-500 rounded-full px-2 py-px font-bold">{c.status}</span>
                      </motion.li>
                    ))}
                    {channels.length === 0 && <li className="text-xs text-zinc-400 italic">No channels selected.</li>}
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Phase 2: CTA */}
          <AnimatePresence>
            {phase >= 2 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mt-8 flex items-center gap-3 flex-wrap">
                <button onClick={onEnter} data-testid="reveal-enter-dashboard"
                  className="inline-flex items-center gap-2 rounded-full ashra-gradient-bg px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-violet-500/20 hover:shadow-xl transition">
                  Enter Command Center <ChevronRight className="h-4 w-4" />
                </button>
                <p className="text-xs text-zinc-500">Your team is live. You can retune the persona anytime from Nexus → Brand Memory.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}


function TypedPersona({ text, enabled }) {
  // Lightweight typewriter for the persona reveal — no external deps.
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    if (!enabled) return;
    setCount(0);
    const step = Math.max(4, Math.floor(text.length / 220));
    let i = 0;
    const t = setInterval(() => {
      i = Math.min(text.length, i + step);
      setCount(i);
      if (i >= text.length) clearInterval(t);
    }, 20);
    return () => clearInterval(t);
  }, [text, enabled]);
  return <span>{text.slice(0, count)}{count < text.length && <span className="animate-pulse text-violet-500">▍</span>}</span>;
}
